import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { Checkbox } from '../../../components/ui/Checkbox';

const MemberTable = ({ members, selectedMembers, onSelectMember, onSelectAll, onSort, sortConfig, onViewMember }) => {
  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { bg: 'bg-success/10', text: 'text-success', label: 'Active' },
      inactive: { bg: 'bg-muted', text: 'text-muted-foreground', label: 'Inactive' },
      frozen: { bg: 'bg-primary/10', text: 'text-primary', label: 'Frozen' },
      expired: { bg: 'bg-error/10', text: 'text-error', label: 'Expired' },
      trial: { bg: 'bg-warning/10', text: 'text-warning', label: 'Trial' }
    };

    const config = statusConfig?.[status] || statusConfig?.inactive;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config?.bg} ${config?.text}`}>
        {config?.label}
      </span>
    );
  };

  const getPaymentBadge = (status) => {
    const statusConfig = {
      paid: { icon: 'CheckCircle2', color: 'text-success', label: 'Paid' },
      pending: { icon: 'Clock', color: 'text-warning', label: 'Pending' },
      overdue: { icon: 'AlertCircle', color: 'text-error', label: 'Overdue' },
      failed: { icon: 'XCircle', color: 'text-error', label: 'Failed' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;
    return (
      <div className={`flex items-center gap-1 ${config?.color}`}>
        <Icon name={config?.icon} size={16} />
        <span className="text-xs font-medium">{config?.label}</span>
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getSortIcon = (column) => {
    if (sortConfig?.column !== column) return 'ChevronsUpDown';
    return sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown';
  };

  const allSelected = members?.length > 0 && selectedMembers?.length === members?.length;
  const someSelected = selectedMembers?.length > 0 && selectedMembers?.length < members?.length;

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      <div className="overflow-x-auto custom-scrollbar">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left w-12">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={(e) => onSelectAll(e?.target?.checked)}
                />
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('name')}
                  className="flex items-center gap-2 text-xs font-semibold text-foreground uppercase tracking-wider hover:text-primary transition-colors"
                >
                  Member
                  <Icon name={getSortIcon('name')} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('membershipType')}
                  className="flex items-center gap-2 text-xs font-semibold text-foreground uppercase tracking-wider hover:text-primary transition-colors"
                >
                  Membership
                  <Icon name={getSortIcon('membershipType')} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('gymName')}
                  className="flex items-center gap-2 text-xs font-semibold text-foreground uppercase tracking-wider hover:text-primary transition-colors"
                >
                  Gym
                  <Icon name={getSortIcon('gymName')} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('status')}
                  className="flex items-center gap-2 text-xs font-semibold text-foreground uppercase tracking-wider hover:text-primary transition-colors"
                >
                  Status
                  <Icon name={getSortIcon('status')} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-xs font-semibold text-foreground uppercase tracking-wider">
                  Payment
                </span>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('lastCheckIn')}
                  className="flex items-center gap-2 text-xs font-semibold text-foreground uppercase tracking-wider hover:text-primary transition-colors"
                >
                  Last Check-In
                  <Icon name={getSortIcon('lastCheckIn')} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-xs font-semibold text-foreground uppercase tracking-wider">
                  Flags
                </span>
              </th>
              <th className="px-4 py-3 text-center w-24">
                <span className="text-xs font-semibold text-foreground uppercase tracking-wider">
                  Actions
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {members?.map((member) => (
              <tr
                key={member?.id}
                className="hover:bg-muted/30 transition-colors cursor-pointer"
                onClick={() => onViewMember(member?.id)}
              >
                <td className="px-4 py-3" onClick={(e) => e?.stopPropagation()}>
                  <Checkbox
                    checked={selectedMembers?.includes(member?.id)}
                    onChange={(e) => onSelectMember(member?.id, e?.target?.checked)}
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-muted">
                      <Image
                        src={member?.avatar}
                        alt={member?.avatarAlt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{member?.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{member?.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">{member?.membershipType}</p>
                    <p className="text-xs text-muted-foreground">Expires: {formatDate(member?.expiryDate)}</p>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => onSort('gymName')}
                    className="flex items-center gap-2 text-xs font-semibold text-foreground uppercase tracking-wider hover:text-primary transition-colors"
                  >
                    {member?.gymName || 'N/A'}
                    <Icon name={getSortIcon('gymName')} size={14} />
                  </button>
                </td>
                <td className="px-4 py-3">
                  {getStatusBadge(member?.status)}
                </td>
                <td className="px-4 py-3">
                  {getPaymentBadge(member?.paymentStatus)}
                </td>
                <td className="px-4 py-3">
                  <p className="text-sm text-foreground">{formatDate(member?.lastCheckIn)}</p>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {member?.flags?.map((flag, index) => (
                      <div
                        key={index}
                        className="w-6 h-6 rounded-full bg-warning/10 flex items-center justify-center"
                        title={flag?.label}
                      >
                        <Icon name={flag?.icon} size={14} color="var(--color-warning)" />
                      </div>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3" onClick={(e) => e?.stopPropagation()}>
                  <div className="flex items-center justify-center gap-1">
                    <button
                      className="p-2 rounded-lg hover:bg-muted transition-colors"
                      title="View Profile"
                      onClick={() => onViewMember(member?.id)}
                    >
                      <Icon name="Eye" size={16} />
                    </button>
                    <button
                      className="p-2 rounded-lg hover:bg-muted transition-colors"
                      title="Edit Member"
                      onClick={() => onViewMember(member?.id)}
                    >
                      <Icon name="Edit" size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MemberTable;